// ====================================================================
// ziven-theme-aura v0.2.10 modifyDrawer.js
// ====================================================================
//
// 移动端 drawer 注入 (< 992px)
// 桌面端 (>= 992px) 走 modifySidebar.js, drawer 内部 ziven-theme-aura 注入的元素被 CSS 隐藏
//   但 vendor top bar (含 logo + 搜索 + 通知 + 头像) 在桌面端继续显示
//
// 顺序 (跟 ziven.ski 1.8 真实 1:1, 跟设计稿 v8 一致, 辉哥 07:42 拍板):
//   user-block (顶部) → 5 stats (紧跟 user-block) → nav section (8 nav) → vendor 4 扩展
//
// v0.2.10 (辉哥 17:44 拍板 A 方案): 未登录也注入 nav section, 1.8 ziven.ski 1:1 复刻
//   - 删 L24-25 `if (!user) return;` 限制
//   - user-block + 5 stats 包到 `if (user) { ... }` 内 (登录态才有)
//   - .App-drawer-aura-logged-in class 也只登录态加 (CSS 用来隐藏论坛名称, 未登录显示 logo)
//   - nav section 任何时候都跑, 1:1 复刻 ziven.ski 1.8 改造的未登录态
//     (vendor IndexSidebar.navItems() 默认给 All Discussions + Tags 2 nav,
//      vendor flarum-tags 也会加 tags nav, 跟 1.8 ziven-pallet-theme 一致)
//   - addAccountNavItems L249-251 已有 `if (!user) return;` 保护, 未登录不追加 3 账号 nav
//
// 登录态检测: drawer 加 .App-drawer-aura-logged-in class
//   CSS 用 .App-drawer-aura-logged-in .Header-title { display: none } 隐藏论坛名称
//
// 桌面端详见 modifySidebar.js
// ====================================================================

import app from 'flarum/common/app';
import { extend } from 'flarum/common/extend';
import { buildUserBlock, buildStatsSection, buildNavSection, renderNavItems } from './buildElements';

// === 注入 user-block + 5 stats + nav section (mobile drawer 模式) ===
function injectMobileSidebar() {
  const user = app.session.user;
  const drawer = document.getElementById('drawer');
  if (!drawer) return;

  // 桌面端走 modifySidebar.js, 这里不注入避免重复
  if (window.matchMedia('(min-width: 992px)').matches) return;

  // === 加 loggedIn class (CSS 用来隐藏论坛名称, 仅登录态加) ===
  if (user) {
    drawer.classList.add('App-drawer-aura-logged-in');

    // === 1. user-block 注入: 顶部 (prepend) ===
    if (!drawer.querySelector('.App-drawer-aura-user-block')) {
      const userBlock = buildUserBlock(user);
      if (drawer.firstChild) {
        drawer.insertBefore(userBlock, drawer.firstChild);
      } else {
        drawer.appendChild(userBlock);
      }
    }

    // === 2. 5 stats 注入: 紧跟 user-block ===
    if (!drawer.querySelector('.App-drawer-aura-stats')) {
      // v0.2.6: 传 user, 改读真实数据
      const statsSection = buildStatsSection(user);
      const userBlock = drawer.querySelector('.App-drawer-aura-user-block');
      if (userBlock && userBlock.nextSibling) {
        drawer.insertBefore(statsSection, userBlock.nextSibling);
      } else if (userBlock) {
        drawer.appendChild(statsSection);
      } else {
        if (drawer.firstChild) {
          drawer.insertBefore(statsSection, drawer.firstChild);
        } else {
          drawer.appendChild(statsSection);
        }
      }
    }
  }

  // === 3. nav section 注入: 在 vendor header 之前 (登录 + 未登录都注入, v0.2.10) ===
  // 顺序 (登录态): 5 stats → nav (8 nav: 5 主体 + 1 separator + 3 账号)
  // 顺序 (未登录态, 1.8 ziven.ski 1:1): nav (2 nav: All Discussions + Tags, vendor flarum-tags 也会加 tags)
  // 注意: ziven.ski 真实顺序: 5 stats → separator → 8 nav, 但我们简化, 不画 separator 在 5 stats 后
  //       (跟 v0.1.0c 一致)
  if (!drawer.querySelector('.App-drawer-aura-nav')) {
    const navSection = buildNavSection();
    // 注入位置: 5 stats 之后 (登录态) 或 drawer 头部 (未登录态) → vendor header 之前
    const statsSection = drawer.querySelector('.App-drawer-aura-stats');
    if (statsSection && statsSection.nextSibling) {
      drawer.insertBefore(navSection, statsSection.nextSibling);
    } else {
      drawer.appendChild(navSection);
    }
  }

  // v0.2.5b: 删 m.redraw() (强制 sync mithril redraw, 跟 vendor 路由 redraw scheduler 死锁 / 重复, 可能触发 SPA 跳路由时 console 错误)
  // vendor mithril 自己调度 redraw, 不需要我们强制触发
}

// === v0.2.8: 路由切换时重新 render nav items (让 vendor LinkButton.isActive 重算) ===
// 移动端 drawer 实际显示, 路由切换时让 vendor LinkButton.isActive 重算
// mithril m.render() 模式: 一次性渲染, mithril m.route() 切换不重画, LinkButton.active 写死在 DOM
// 修法: 监听 mithril 路由变化 (popstate + monkey-patch pushState), 重新 m.render 触发 LinkButton 重新 initAttrs
// v0.2.8 修 (重要): mithril 0.2.x 路由切换是异步的, m.render 同步调用时 m.route.get() 仍返回旧值
//   - 修法: 在 pushState 调用时 (monkey-patch 内部) 同步缓存 currentPath 到 window
//   - refreshNavActive 读 window.__ziven_aura_current_path__ 传给 renderNavItems
//   - renderNavItems 给 vendor LinkButton 强制覆盖 active 属性
function refreshNavActive() {
  console.log('[ziven-theme-aura] refreshNavActive called, currentPath=', window.__ziven_aura_current_path__);
  setTimeout(() => {
    const drawer = document.getElementById('drawer');
    if (!drawer) return;
    const navSection = drawer.querySelector('.App-drawer-aura-nav');
    if (!navSection) return;
    try {
      // 重新 render nav items (让 vendor LinkButton.isActive 重算, 因为 mithril 路由变了)
      // v0.2.8 修: 传 currentPath (m.route.get() 在异步路由切换期间可能返回旧值)
      renderNavItems(navSection, window.__ziven_aura_current_path__);
    } catch (e) {
      console.error('[ziven-theme-aura] refreshNavActive (mobile) failed:', e);
    }
  }, 0);
}

// === 监听登录状态 + viewport 变化 ===
function setupObserver() {
  setTimeout(injectMobileSidebar, 100);

  // 监听 mithril redraw
  extend('flarum/common/Component', 'onupdate', function () {
    if (document.getElementById('drawer') && !document.querySelector('.App-drawer-aura-user-block')) {
      injectMobileSidebar();
    }
  });

  // 监听 viewport 切换 (resize, 移动端 ↔ 桌面端切换)
  window.matchMedia('(min-width: 992px)').addEventListener('change', () => {
    // viewport 切换时, 清空 ziven-theme-aura 注入的元素让另一边接管
    const drawer = document.getElementById('drawer');
    if (drawer) {
      drawer.querySelectorAll('.App-drawer-aura-user-block, .App-drawer-aura-stats, .App-drawer-aura-nav').forEach(el => el.remove());
    }
    const sidebar = document.querySelector('.App-sidebar-aura');
    if (sidebar) sidebar.remove();
    // 触发自定义事件, 让 modifySidebar 重新评估
    window.dispatchEvent(new CustomEvent('ziven-aura-viewport-changed'));
    // 自己也重新评估 (mobile 端重新注入)
    if (!window.matchMedia('(min-width: 992px)').matches) {
      setTimeout(injectMobileSidebar, 100);
    }
  });

  // v0.2.8 改回: 监听 mithril 路由变化, 重新 render nav items
  // m.render() 模式不会自动重画, 需要手动触发让 LinkButton 重新 initAttrs
  window.addEventListener('popstate', refreshNavActive);
  // v0.2.8 修 (重要, SOP 133 候选): chain pattern 修 monkey-patch
  //   - 之前用 `window.__ziven_aura_patched__` guard, 但被 Playwright 客户端先 patch 占用
  //   - ziven-theme-aura 的 listener 永远不被调用, 路由切换时 active 不更新
  //   - 修法: 改 guard name `__ziven_aura_patched_v3__` + chain pattern
  //     找到链头 `currentPush.__ziven_aura_original_v3__ || currentPush`,
  //     不会覆盖 Playwright 客户端的 patch, 链头永远指向原生 pushState
  //   - 跟 modifySidebar.js 共享同一个 guard, 避免双 patch
  //   - 同时在 pushState 调用的瞬间, 同步缓存 currentPath 到 window.__ziven_aura_current_path__
  //     (因为 mithril 0.2.x 路由切换是异步的, m.render 同步调用时 m.route.get() 仍返回旧值)
  if (!window.__ziven_aura_patched_v3__) {
    window.__ziven_aura_patched_v3__ = true;
    const currentPush = history.pushState;
    const originalPush = currentPush.__ziven_aura_original_v3__ || currentPush;
    const wrappedPush = function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      // args[2] = URL (pushState state, title, url)
      // 但 mithril 调 pushState 时 url 可能是 undefined (用当前 m.route.get())
      // 兜底用 window.location.pathname (pushState 调用后 location 还没变, 用 args[2] 或 m.route.get())
      originalPush.apply(this, args);
      // v0.2.8: 同步缓存 currentPath 给 refreshNavActive 用
      window.__ziven_aura_current_path__ = args[2] || m.route.get() || window.location.pathname;
      window.dispatchEvent(new CustomEvent('ziven-aura-route-changed'));
    };
    wrappedPush.__ziven_aura_original_v3__ = originalPush;
    history.pushState = wrappedPush;
    const currentReplace = history.replaceState;
    const originalReplace = currentReplace.__ziven_aura_original_v3__ || currentReplace;
    const wrappedReplace = function () {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }
      originalReplace.apply(this, args);
      window.__ziven_aura_current_path__ = args[2] || m.route.get() || window.location.pathname;
      window.dispatchEvent(new CustomEvent('ziven-aura-route-changed'));
    };
    wrappedReplace.__ziven_aura_original_v3__ = originalReplace;
    history.replaceState = wrappedReplace;

    // 初始化 currentPath (页面首次加载时)
    window.__ziven_aura_current_path__ = window.location.pathname;
  }
  window.addEventListener('ziven-aura-route-changed', refreshNavActive);
}
export default function modifyDrawer() {
  setupObserver();
}